import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useApiClient } from 'utils/apiClient';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import { useLocalStorageState } from 'ahooks';
import { publishUIMessage } from 'Common/notifications';
import { defineMessage, useIntl } from 'react-intl';
import { handleErrorNotification } from 'Common/utils';

// eslint-disable-next-line import/prefer-default-export
export const useLoginUser = () => {
  const apiClient = useApiClient();
  const [_, setAccount] = useLocalStorageState<AccountPersistInfo>('account');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      username: string;
      pin: string;
      accountToken: string;
    }) => apiClient.CoreApi.loginUser(input),
    onSuccess: (
      response,
      input: { username: string; pin: string; accountToken: string }
    ) => {
      if (response.token !== null) {
        setAccount(
          (prev) =>
            ({
              ...prev,
              userToken: response.token,
              pin: input.pin,
              accountSuccessful: true,
              tokenSetAt: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            }) as AccountPersistInfo
        );
        queryClient.setQueryData(['account', 'userToken'], response.token);
        queryClient.removeQueries({ queryKey: ['common', 'api'] });
      }
    },
  });
};

export const useAccountLogin = () => {
  const apiClient = useApiClient();
  const [_, setAccount] = useLocalStorageState<AccountPersistInfo>('account');

  return useMutation({
    mutationFn: apiClient.CoreApi.loginAccount,
    onSuccess: (data, variables) => {
      if (data.token) {
        setAccount(
          (prev) =>
            ({
              ...prev,
              email: variables.email,
              accountToken: data.token,
              userSuccessful: true,
            }) as AccountPersistInfo
        );

        apiClient.CoreApi.loginCookie({
          email: variables.email,
          password: variables.password,
        });
      }
    },
  });
};

export const useResetPassword = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  return useMutation({
    mutationFn: apiClient.CoreApi.resetPassword,
    onSuccess: () => {
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage: 'Your password was successfully updated',
        }),
        { duration: 8 }
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useRequestNewPassword = () => {
  const apiClient = useApiClient();
  const intl = useIntl();

  return useMutation({
    mutationFn: apiClient.CoreApi.requestNewPassword,
    onSuccess: () => {
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage:
            'You will receive an email with instructions to reset your password.',
        }),
        { duration: 8 }
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};
